import { useQueries } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import { INSIGHTS_STALE_TIME } from '@/components/loop-dashboard/data/constants';
import { HealthApi } from '@/lib/api/dashboard/health';
import type { ItemHealthByItemIdsResponse } from '@/lib/api/types/dashboard/health';
import { insightsQueryKeys } from '@/lib/query-keys/insights';

const SYSTEM_PROGRESS_CHUNK_SIZE = 200;

function chunkArray<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

export function useSystemProgress({
  itemIds,
  enabled = true,
}: {
  itemIds: number[];
  enabled?: boolean;
}): { progressMap: Map<number, number>; isLoading: boolean } {
  const chunks = useMemo(
    () => (enabled ? chunkArray(itemIds, SYSTEM_PROGRESS_CHUNK_SIZE) : []),
    [itemIds, enabled],
  );

  // Stable reference: only produce a new Map when the underlying data actually changes.
  // useQueries/combine runs on every render, so without this the progressMap reference
  // would change every render even when health data is unchanged, causing unnecessary
  // downstream re-renders in components that depend on it.
  const prevDataUpdatedAtRef = useRef<number[]>([]);
  const prevProgressMapRef = useRef<Map<number, number>>(new Map());

  return useQueries({
    queries: chunks.map((chunk) => ({
      queryKey: insightsQueryKeys.health.byItemIds(chunk),
      queryFn: () => HealthApi.getItemHealth({ itemIds: chunk }),
      enabled,
      staleTime: INSIGHTS_STALE_TIME,
      placeholderData: (previousData: ItemHealthByItemIdsResponse | undefined) => previousData,
      retry: false,
    })),
    combine: (results) => {
      const updatedAts = results.map((r) => r.dataUpdatedAt);
      const hasChanged =
        updatedAts.length !== prevDataUpdatedAtRef.current.length ||
        updatedAts.some((t, i) => t !== prevDataUpdatedAtRef.current[i]);

      if (hasChanged) {
        const progressMap = new Map<number, number>();
        for (const result of results) {
          for (const itemHealth of result.data?.data ?? []) {
            progressMap.set(itemHealth.itemId, itemHealth.progress / 100);
          }
        }
        prevDataUpdatedAtRef.current = updatedAts;
        prevProgressMapRef.current = progressMap;
      }

      return {
        progressMap: prevProgressMapRef.current,
        isLoading: results.some((r) => r.isLoading),
      };
    },
  });
}
